/**
 * lazyPage — route-level code splitting (Oct 2026).
 *
 * The whole SPA used to ship as ONE JS chunk, which outgrew the PWA precache
 * cap (vite.config.ts `maximumFileSizeToCacheInBytes`) and failed the build.
 * Every tool page is now loaded on demand:
 *
 *   const InvoiceDetailPage = lazyPage(() => import('../invoices/pages/InvoiceDetailPage'), 'InvoiceDetailPage');
 *   const SubscriptionsHealthPage = lazyPage(() => import('./SubscriptionsHealthPage'), 'default');
 *
 * The returned component carries its OWN <Suspense> boundary, so it can be
 * dropped anywhere a page element goes (App.tsx routes, the analytics /
 * subscriptions / operations registries) and the surrounding shell (sidebar,
 * section tabs) stays on screen while the chunk loads. The chunks are still
 * precached by the service worker, so the installed app works offline.
 *
 * A failed chunk load (stale bundle after a deploy) rejects inside React.lazy
 * and is thrown at render, so it reaches the app-wide ErrorBoundary
 * (components/ErrorBoundary.tsx), which clears the SW cache and reloads once.
 *
 * RULE: never static-import a page module anywhere the shell imports from
 * (Layout, nav, sections) — that pulls it back into the main chunk.
 */
import { lazy, Suspense, type ComponentProps, type ComponentType } from 'react';
import { Loading } from '../components/Loading';

export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  type C = M[K] extends ComponentType<any> ? M[K] : never; // eslint-disable-line @typescript-eslint/no-explicit-any
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] as unknown as ComponentType<any> }))); // eslint-disable-line @typescript-eslint/no-explicit-any
  function LazyPage(props: ComponentProps<C>) {
    return (
      <Suspense fallback={<Loading />}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  LazyPage.displayName = `lazyPage(${String(name)})`;
  return LazyPage;
}
